import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

import type { DocumentHistory } from '../config';
import { readHistoryShortcut } from '../lib';

const MODAL_ROOT = '[data-modal-root]';

// The note's undo and redo keys also act while focus has left the note but
// stays on its surface: the page, or the modal or pop-up the note sits in.
// A text box keeps the keys for its own text; inside the note, its document
// and frame take them first.
export const useSurfaceHistoryKeys = (
  rootRef: RefObject<HTMLElement>,
  history: DocumentHistory | undefined,
) => {
  const latest = useRef(history);

  useLayoutEffect(() => {
    latest.current = history;
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      const target = event.target instanceof Element ? event.target : null;
      if (
        event.defaultPrevented ||
        !latest.current ||
        !root ||
        (target && root.contains(target)) ||
        (target &&
          target !== document.body &&
          target.closest(MODAL_ROOT) !== root.closest(MODAL_ROOT))
      ) {
        return;
      }
      const action = readHistoryShortcut(event);
      if (!action) {
        return;
      }
      event.preventDefault();
      if (action === 'undo') {
        latest.current.undo();
      } else {
        latest.current.redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rootRef]);
};
